import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import "./SolowaySettings.css";
import { FiEdit, FiTrash2 } from "react-icons/fi";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

export default function SolowaySettings() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Fetch videos
  const fetchVideos = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/admin/solowaysettings/videos`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to fetch videos");
      setVideos(data);
    } catch (e) {
      setErr(e.message || "Failed to load videos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVideos();
  }, []);

  // Upload video
  const handleUpload = async () => {
    const { value: formValues } = await Swal.fire({
      title: "Upload Video",
      html: `
        <div style="display:flex; flex-direction:column; gap:12px; align-items:center; justify-content: center; width:100%; max-width:400px; margin:0 auto;">
            <input id="swal-title" class="swal2-input" placeholder="Title" style="width:100%; box-sizing:border-box;">
            <textarea id="swal-desc" class="swal2-textarea" placeholder="Description" style="width:100%; box-sizing:border-box; resize:none;"></textarea>
            <label for="swal-file" style="
                display:flex; 
                align-items:center; 
                justify-content:center; 
                width:100%; 
                height:50px; 
                border:2px dashed #4338ca; 
                border-radius:12px; 
                cursor:pointer; 
                color:#3b82f6;
                font-weight:600;
                font-size:16px;
                transition: background 0.2s, color 0.2s;
                text-align:center;
            " 
            onmouseover="this.style.background='#4338ca'; this.style.color='#fff';" 
            onmouseout="this.style.background=''; this.style.color='#3b82f6';">
                Click or drag file to upload
            </label>
            <input id="swal-file" type="file" accept="video/*" style="display:none;">
        </div>
        `,
      showCancelButton: true,
      confirmButtonText: "Upload",
      customClass: {
        confirmButton: "custom-swal-confirm",
        cancelButton: "custom-swal-cancel",
      },
      preConfirm: () => {
        const title = document.getElementById("swal-title").value;
        const desc = document.getElementById("swal-desc").value;
        const file = document.getElementById("swal-file").files[0];
        if (!title || !file) {
          Swal.showValidationMessage("Title and video file are required");
          return null;
        }
        return { title, desc, file };
      },
    });

    if (!formValues) return;

    try {
      const formData = new FormData();
      formData.append("title", formValues.title);
      formData.append("description", formValues.desc);
      formData.append("video", formValues.file);

      const res = await fetch(`${API}/admin/solowaysettings/videos`, {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to upload video");

      Swal.fire({ title: "Uploaded!", text: "Video uploaded successfully.", icon: "success", timer: 1200, showConfirmButton: false });
      fetchVideos();
    } catch (e) {
      Swal.fire("Error", e.message, "error");
    }
  };

  // Delete video
  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This will permanently delete the video",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
      customClass: {
        confirmButton: "custom-swal-confirm",
        cancelButton: "custom-swal-cancel",
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await fetch(`${API}/admin/solowaysettings/videos/${id}`, {
            method: "DELETE",
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.message || "Failed to delete");
          Swal.fire({ title: "Deleted!", text: "Video deleted successfully.", icon: "success", timer: 1200, showConfirmButton: false });
          fetchVideos();
        } catch (e) {
          Swal.fire({ title: "Error", text: e.message, icon: "error", timer: 2000, showConfirmButton: false });
        }
      }
    });
  };

  // Edit video (title/description)
  const handleEdit = async (video) => {
    const { value: formValues } = await Swal.fire({
      title: "Update Video",
      html:
        `<div style="display:flex; flex-direction:column; gap:12px; align-items:center; justify-content: center; width:100%; max-width:400px; margin:0 auto;">
            <input id="swal-title" class="swal2-input" placeholder="Title" style="width:100%; box-sizing:border-box;" value="${video.title}">
            <textarea id="swal-desc" class="swal2-textarea" placeholder="Description" style="width:100%; box-sizing:border-box; resize:none;">${video.description || ""}</textarea>
        </div>`,
      showCancelButton: true,
      confirmButtonText: "Update",
      customClass: {
        confirmButton: "custom-swal-confirm",
        cancelButton: "custom-swal-cancel",
      },
      preConfirm: () => {
        const title = document.getElementById("swal-title").value;
        const desc = document.getElementById("swal-desc").value;

        if (!title) {
          Swal.showValidationMessage("Title is required");
          return null;
        }

        // Only include description if not empty
        const payload = { title };
        if (desc !== undefined && desc !== null) payload.description = desc;

        return payload;
      },
    });

    if (!formValues) return;

    try {
      const res = await fetch(`${API}/admin/solowaysettings/videos/${video.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formValues),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to update");
      Swal.fire({ title: "Updated!", text: "Video updated successfully.", icon: "success", timer: 1200, showConfirmButton: false });
      fetchVideos();
    } catch (e) {
      Swal.fire({ title: "Error", text: e.message, icon: "error", timer: 2000, showConfirmButton: false });
    }
  };

  if (loading) return <p style={{ padding: 24 }}>Loading videos…</p>;
  if (err) return <p style={{ padding: 24, color: "crimson" }}>{err}</p>;

  return (
    <div className="wd-page">
      <div className="wd-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 className="wd-title">Video Management</h2>
        <button className="btn ghost" onClick={handleUpload}>+ Upload Video</button>
      </div>

      <section className="card table-card">
        <table className="table">
          <thead>
            <tr>
              <th>Midea ID</th>
              <th>Title</th>
              <th>Description</th>
              <th>Preview</th>
              <th>Uploaded At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {videos.map((v) => (
              <tr key={v.id}>
                <td>{v.vid}</td>
                <td>{v.title}</td>
                <td className="muted">{v.description || "—"}</td>
                <td>
                  <video src={`${API}${v.file_path}`} width="160" controls />
                </td>
                <td className="muted">{new Date(v.created_at).toLocaleString()}</td>
                <td>
                  <FiEdit
                    style={{ cursor: "pointer", color: "#3b82f6", marginRight: 12 }}
                    size={18}
                    onClick={() => handleEdit(v)}
                    title="Update"
                  />
                  <FiTrash2
                    style={{ cursor: "pointer", color: "#ef4444" }}
                    size={18}
                    onClick={() => handleDelete(v.id)}
                    title="Delete"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {videos.length === 0 && <p className="dots">No videos uploaded yet.</p>}
      </section>
    </div>
  );
}
